import { useState } from "react";
import { useSession } from "@/services/auth";
import { Button, Card, CardBody, Chip, Spinner } from "@heroui/react";
import { useToast } from "@/hooks/use-toast";
import type { PaymentMethod } from "@/api.types";
import { useTranslation } from "react-i18next";
import {
  useDeletePaymentMethod,
  useMakePaymentMethodDefault,
  usePaymentMethods,
} from "@/api/use-payment-methods-api";
import { PaymentMethodsError } from "./payment-methods-error";
import { AddPaymentMethodDialog } from "./add-payment-method.dialog";

const getCardBrandLogo = (brand?: string): string => {
  const baseUrl =
    "https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards";
  switch (brand?.toLowerCase()) {
    case "mastercard":
      return `${baseUrl}/mastercard.svg`;
    case "visa":
      return `${baseUrl}/visa.svg`;
    case "amex":
    case "american express":
      return `${baseUrl}/american-express.svg`;
    case "diners":
    case "diners club":
      return `${baseUrl}/diners.svg`;
    case "discover":
      return `${baseUrl}/discover.svg`;
    case "jcb":
      return `${baseUrl}/jcb.svg`;
    case "unionpay":
      return `${baseUrl}/unionpay.svg`;
    default:
      return `${baseUrl}/card-generic.svg`;
  }
};

const PaymentMethodCard = ({
  method,
  onSetDefault,
  onDelete,
}: {
  method: PaymentMethod;
  onSetDefault: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation("app");

  return (
    <Card shadow="sm">
      <CardBody className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={getCardBrandLogo(method.brand)}
              alt={method.brand ?? t("settings.cardAlt")}
              className="h-8 w-auto shrink-0"
            />
            <div className="flex flex-col min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">•••• {method.last4}</span>
                {method.isDefault && (
                  <Chip color="primary" variant="flat" size="sm">
                    {t("settings.default")}
                  </Chip>
                )}
              </div>
              <p className="text-sm text-default-500">
                {t("paymentMethodsScreen.expires", {
                  month: method.expiryMonth,
                  year: method.expiryYear,
                })}
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {!method.isDefault && (
              <Button
                variant="flat"
                size="sm"
                className="flex-1 sm:flex-initial"
                onPress={onSetDefault}
              >
                {t("actions.makeDefault")}
              </Button>
            )}
            <Button
              color="danger"
              size="sm"
              className="flex-1 sm:flex-initial"
              variant="flat"
              onPress={onDelete}
            >
              {t("actions.remove")}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default function PaymentMethodScreen() {
  const { t } = useTranslation("app");
  const { customer, isLoading: isSessionLoading } = useSession();
  const { toast } = useToast();
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);

  const {
    data: billingData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = usePaymentMethods();

  const setDefaultPaymentMethodMutation = useMakePaymentMethodDefault({
    onSuccess: () => {
      toast({
        title: t("settings.toast.defaultUpdated"),
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.toast.defaultUpdateError"),
      });
    },
  });

  const deletePaymentMethodMutation = useDeletePaymentMethod({
    onSuccess: () => {
      toast({
        title: t("settings.toast.paymentRemoved"),
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.toast.paymentRemoveError"),
      });
    },
  });

  if (isSessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!customer?.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-default-500">
          {t("paymentMethodsScreen.loginRequired")}
        </p>
      </div>
    );
  }

  return (
    <div className="container max-w-xl space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">
          {t("paymentMethodsScreen.title")}
        </h2>
        <p className="text-sm text-default-500">
          {t("paymentMethodsScreen.lede")}
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h2 className="text-lg font-semibold">
            {t("paymentMethodsScreen.yourMethods")}
          </h2>
          <Button
            color="primary"
            className="w-full sm:w-auto"
            onPress={() => setIsAddPaymentMethodOpen(true)}
          >
            {t("paymentMethodsScreen.addDialogTitle")}
          </Button>
        </div>
        {isLoading ? (
          <Card shadow="sm">
            <CardBody className="p-6 flex justify-center">
              <Spinner size="md" />
            </CardBody>
          </Card>
        ) : isError ? (
          <PaymentMethodsError
            onRetry={() => void refetch()}
            isRetrying={isFetching}
          />
        ) : !billingData?.paymentMethods?.length ? (
          <Card shadow="sm">
            <CardBody className="p-6">
              <p className="text-default-500">
                {t("paymentMethodsScreen.empty")}
              </p>
            </CardBody>
          </Card>
        ) : (
          billingData.paymentMethods
            .slice()
            .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0))
            .map((method: PaymentMethod) => (
              <PaymentMethodCard
                key={method.id}
                method={method}
                onSetDefault={() =>
                  setDefaultPaymentMethodMutation.mutate(method.id)
                }
                onDelete={() => deletePaymentMethodMutation.mutate(method.id)}
              />
            ))
        )}
      </div>

      <AddPaymentMethodDialog
        open={isAddPaymentMethodOpen}
        onOpenChange={setIsAddPaymentMethodOpen}
      />
    </div>
  );
}
